import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {
    useState
} from 'react';

import RIQButton from '../../../../components/RIQButton.jsx';
import MetricResultDisplay from './MetricResultDisplay.jsx';

function applySubstitutions(object, substitutions) {
    if (substitutions && _.isObject(object)) {
        _.each(object, (value, key) => {
            if (_.isString(value) && value.startsWith('$')) {
                const substitutionKey = value.substring(1);
                if (substitutions[substitutionKey]) {
                    if (substitutionKey === 'timePeriod') {
                        object[key] = {
                            startDate: substitutions[substitutionKey].mainPeriodStart,
                            endDate: substitutions[substitutionKey].mainPeriodEnd,
                            timezone: RivalIQ.state.landscape.get('timezone')
                        };
                    } else {
                        object[key] = substitutions[substitutionKey];
                    }
                }
            } else if (_.isObject(value)) {
                object[key] = applySubstitutions(value, substitutions);
            }
        });
    }

    return object;
}

function getMetricValidationError(metricJSONStr, substitutions) {
    metricJSONStr = _.trim(metricJSONStr);
    if (_.isEmpty(metricJSONStr)) {
        return {
            isValid: false
        };
    }

    let metricRequest;
    try {
        metricRequest = JSON.parse(metricJSONStr);
        metricRequest = applySubstitutions(metricRequest, substitutions);
    } catch {
        return {
            isValid: false,
            errorString: 'Invalid JSON'
        };
    }

    const validationErrors = RivalIQ.metrics.new.MetricRequest.validateMetricRequest(metricRequest);
    return {
        isValid: _.isEmpty(validationErrors),
        errorString: validationErrors
    };
}

const DEFAULT_METRICS = {
    dataSourceId: 'FACEBOOK_PUBLIC_POST_DATA',
    dimensions: [],
    measure: 'sum(engagement_total)',
    filters: {
        company: {
            landscapeId: '$landscapeId'
        },
        publicPost: {},
        timePeriod: '$timePeriod'
    }
};

function parseMetricString(metricJSONStr, substitutions) {
    let parsed = JSON.parse(metricJSONStr);
    if (!_.isArray(parsed)) {
        parsed = [parsed];
    }

    parsed = _.map(parsed, metricRequest => applySubstitutions(metricRequest, substitutions));
    return parsed;
}

export default function MetricsExplorer({substitutions}) {
    const [metricJSONStr, setMetricJSONStr] = useState(JSON.stringify(DEFAULT_METRICS, null, 4));
    const [metricRequests, setMetricRequests] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const {isValid: isMetricValid, errorString: metricErrorString} = getMetricValidationError(metricJSONStr, substitutions);
    let isDirty = true;
    if (isSubmitted) {
        try {
            const parsed = parseMetricString(metricJSONStr, substitutions);
            isDirty = !_.isEqual(parsed, metricRequests);
        } catch {
            // ignore
        }
    }

    const onMetricJSONStrChange = (e) => {
        setMetricJSONStr(e.target.value);
    };

    const onTextAreaBlur = (e) => {
        try {
            setMetricJSONStr(JSON.stringify(JSON.parse(e.target.value), null, 4));
        } catch {
            // ignore
        }
    };

    const onSubmit = () => {
        if (isMetricValid) {
            const parsed = parseMetricString(metricJSONStr, substitutions);
            setMetricRequests(parsed);
            setIsSubmitted(true);
        }
    };

    return (
        <div className='tlw-p-4'>

            <textarea
                className='tlw-min-h-[300px] tlw-w-full tlw-rounded-default tlw-border tlw-border-solid tlw-border-gray-600 tlw-outline-0 tlw-ring-transparent focus_tlw-border-blue-500 focus_tlw-shadow-inner'
                value={metricJSONStr}
                onChange={onMetricJSONStrChange}
                onBlur={onTextAreaBlur}/>

            <div className='tlw-flex tlw-w-full tlw-flex-row tlw-items-center tlw-gap-[10px]'>
                <span
                    className='tlw-grow tlw-text-red-500'>
                    {metricErrorString ?? ''}
                </span>

                <RIQButton
                    className='tlw-px-[12px] tlw-py-[6px]'
                    disabled={!isMetricValid}
                    onClick={onSubmit}
                    variant='primary'>
                    Submit
                </RIQButton>
            </div>

            { isSubmitted &&
                <MetricResultDisplay
                    metricRequests={metricRequests}
                    className={isDirty ? 'tlw-my-8 tlw-opacity-50' : 'tlw-my-8'}
                />
            }
        </div>
    );
}

MetricsExplorer.propTypes = {
    substitutions: PropTypes.object
};
